import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, shareReplay } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import type { BaseMetaState } from '@core/ngrx-legacy';
import {
  ReportsDetailsActions,
  ReportsDetailsSelectors,
  ReportsDetailsStoredValue,
} from '@core/reports-data';
import type { DetailsDimensionsData, Dimension, ReportDetailsFragment } from '@core/reports-types';
import { EMPTY_VALUE, NONE_VALUE } from '@core/reports-types';

@Injectable({
  providedIn: 'root',
})
export class ReportsDetailsFacade {
  public static getDetailsFragment({
    primary,
    secondary: initialSecondary,
    dataset,
  }: DetailsDimensionsData): ReportDetailsFragment {
    const sec = initialSecondary ?? [];
    let secondary: Array<Dimension & { inverse?: boolean }> = Array.isArray(sec) ? sec : [sec];

    if (secondary.length > dataset.dimensions.length) {
      let ds = dataset;
      while (ds.parent) {
        ds = ds.parent;
      }
      const set = new Set([...secondary.map(d => d.id)]);
      if (primary.id !== EMPTY_VALUE && primary.id !== NONE_VALUE) {
        set.add(primary.id);
      }
      secondary = ds.dimensions
        .slice(1)
        .filter(d => !set.has(d.id))
        .map(x => ({ ...x, inverse: true }));
    }

    return { primary, secondary };
  }

  private readonly store = inject(Store);

  public fetchDetails(
    key: string,
    data: unknown,
    fragment: ReportDetailsFragment,
    page: number,
  ): void {
    this.store.dispatch(
      ReportsDetailsActions.fetchDetails({ payload: { key, data: <any>data, fragment, page } }),
    );
  }

  public getDetails(key: string): Observable<ReportsDetailsStoredValue> {
    return this.store
      .select(ReportsDetailsSelectors.selectDetails(key))
      .pipe(filterBoolean, shareReplay(1));
  }

  public getDetailsMeta(key: string): Observable<BaseMetaState> {
    return this.store
      .select(ReportsDetailsSelectors.selectDetailsMeta(key))
      .pipe(filterBoolean, shareReplay(1));
  }
}
